import PluginController from './plugin.controller.js';
import CoreConfigManager from './core.config.manager.js';

export default class Core {
    /**
     * @param {HTMLElement} el
     * @param {Object} options
     * @param {string} pluginName
     */
    constructor(el, options = {}, pluginName = false) {
        if (!this._isNode(el)) {
            throw new Error('Es ist kein gültiges Element angegeben');
        }

        this.el = el;
        this._pluginName = pluginName || this.constructor.name;
        this.options = this._mergeOptions(options);
        this._initialized = false;

        this._registerInstance();
        this._init();
    }

    init() {
        throw new Error(`Die "init" Methode für die Klasse "${this._pluginName}" ist nicht definiert.`);
    }

    _isNode(el) {
        return el instanceof HTMLElement || el === document || el === window;
    }

    _init() {
        if (!this._initialized) {
            this.init();
            this._initialized = true;
        }
    }

    _hasAttribute(element, attribute) {
        if (!this._isNode(element)) {
            throw new Error('Es ist kein gültiges Element angegeben');
        }

        if (typeof element.hasAttribute !== 'function') {
            return false;
        }

        return element.hasAttribute(attribute);
    }

    _getAttribute(element, attribute, strict = true) {
        if (strict && this._hasAttribute(element, attribute) === false) {
            throw new Error(`Das benötigte "${attribute}" existiert nicht!`);
        }

        if (typeof element.getAttribute !== 'function') {
            if (strict) {
                throw new Error(
                    'Diese Node unterstützt nicht die getAttribute Funktion!');
            }
            return undefined;
        }
        return element.getAttribute(attribute);
    }

    _parseAnalyze(value) {
        try {
            value = value.replace(',', '.');
            return JSON.parse(value);
        }
        catch {
            return value.toString();
        }
    }

    _stringHelper(string, separator = '') {
        return string.split(separator).map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('').replace(/^./,
            match => match.toLowerCase());
    }

    _getDataAttribute(element, key, strict = true) {
        const parsedKey = this._stringHelper(key.replace(/^data-/, ''), '-');
        if (!this._isNode(element)) {
            if (strict) throw new Error('Es ist kein gültiges Element angegeben!');
            return undefined;
        }

        if (typeof element.dataset === 'undefined') {
            if (strict) {
                throw new Error('Diese Node unterstützt kein dataset attribute!');
            }
            return undefined;
        }

        const attribute = element.dataset[parsedKey];
        if (typeof attribute === 'undefined') {
            if (strict) {
                throw new Error(`Das benötigte Attribut "${key}" existiert nicht am Element: ${element}!`);
            }
            return attribute;
        }

        return this._parseAnalyze(attribute);
    }

    static deepMerge(...objects) {
        return objects.reduce((prev, obj) => {
            Object.keys(obj).forEach(key => {
                const pVal = prev[key];
                const oVal = obj[key];

                if (Array.isArray(pVal) && Array.isArray(oVal)) {
                    prev[key] = pVal.concat(...oVal);
                } else if (typeof pVal === 'object' && typeof oVal === 'object') {
                    prev[key] = Core.deepMerge(pVal, oVal);
                } else {
                    prev[key] = oVal;
                }
            });
            return prev;
        }, {});
    }

    _mergeOptions(options) {
        const dashedPluginName = this._pluginName.replace(/([A-Z])/g, '$1').toLowerCase();
        const dataAttributeConfig = this._getDataAttribute(this.el, `data-${dashedPluginName}-config`, false);
        const dataAttributeOptions = this._getAttribute(this.el, `data-${dashedPluginName}-options`, false);

        let configOptions = {};
        if (dataAttributeConfig) {
            const configFromManager = CoreConfigManager.retrieve(this._pluginName, dataAttributeConfig);
            if (typeof configFromManager === 'object' && configFromManager !== null && !Array.isArray(configFromManager)) {
                configOptions = configFromManager;
            }
        }

        let parsedDataAttributeOptions = {};
        if (dataAttributeOptions) {
            try {
                const parsed = JSON.parse(dataAttributeOptions);
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                    parsedDataAttributeOptions = parsed;
                }
            }
            catch (e) {
                console.error(`Fehler beim Parsen von data-${dashedPluginName}-options: ${e}`);
                throw e;
            }
        }

        const mergeableOptions = [this.constructor.options, this.options, options, configOptions, parsedDataAttributeOptions].map(option => {
            return (typeof option === 'object' && option !== null && !Array.isArray(option)) ? option : {};
        });

        // Verwenden von Core.deepMerge, um alle validen Objekte zu vereinen
        return Core.deepMerge(...mergeableOptions);
    }

    _registerInstance() {
        const elementPluginInstances = PluginController.retrievePluginInstancesFromElement(this.el);
        elementPluginInstances.set(this._pluginName, this);

        const plugin = PluginController.fetchPlugin(this._pluginName, false);
        if (plugin && plugin.get('instances')) {
            plugin.get('instances').push(this);
        } else {
            console.warn(`Plugin ${this._pluginName} ist nicht im PluginController registriert.`);
        }
    }

    update() {

    }

    _update() {
        if (!this._initialized) {
            return;
        }

        this.update();
    }
}
